import { dia, ui, setTheme, util, elementTools, format } from '@joint/plus';
import { Link, cellNamespace } from './shapes';
import { Decorator } from './highlighters';
import { SourceArrowhead, TargetArrowhead, Button } from './link-tools';
import { routerNamespace } from './routers';
import { anchorNamespace } from './anchors';
import { loadExample } from './example';
export var init = function () {
    setTheme('material');
    var toolbarHeight = 50;
    var canvas = document.getElementById('canvas');
    var graph = new dia.Graph({}, { cellNamespace: cellNamespace });
    var paper = new dia.Paper({
        model: graph,
        width: 1000,
        height: 550,
        gridSize: 10,
        async: true,
        frozen: true,
        cellViewNamespace: cellNamespace,
        background: { color: '#F3F7F6' },
        magnetThreshold: 'onleave',
        moveThreshold: 5,
        clickThreshold: 5,
        linkPinning: false,
        interactive: {
            linkMove: false,
            elementMove: false
        },
        markAvailable: true,
        snapLinks: { radius: 40 },
        routerNamespace: routerNamespace,
        defaultRouter: {
            name: 'mapping',
            args: { padding: 30 }
        },
        defaultConnectionPoint: { name: 'anchor' },
        anchorNamespace: anchorNamespace,
        defaultAnchor: { name: 'mapping' },
        defaultConnector: {
            name: 'jumpover',
            args: { jump: 'cubic' }
        },
        highlighting: {
            magnetAvailability: {
                name: 'addClass',
                options: {
                    className: 'record-item-available'
                }
            },
            connecting: {
                name: 'stroke',
                options: {
                    padding: 8,
                    attrs: {
                        'stroke': 'none',
                        'fill': '#7c68fc',
                        'fill-opacity': 0.2
                    }
                }
            }
        },
        defaultLink: function () {
            return new Link();
        },
        validateConnection: function (sv, sm, tv, tm, end) {
            var svModel = sv.model;
            var tvModel = tv.model;
            if (sv === tv)
                return false;
            if (svModel.isLink() || tvModel.isLink())
                return false;
            if (end === 'target') {
                var targetItemId = tv.findAttribute('item-id', tm);
                if (!tvModel.isItemInView(targetItemId))
                    return false;
                return (tvModel.getItemSide(targetItemId) !== 'right');
            }
            var sourceItemId = sv.findAttribute('item-id', sm);
            if (!svModel.isItemInView(sourceItemId))
                return false;
            return (svModel.getItemSide(sourceItemId) !== 'left');
        }
    });
    var scroller = new ui.PaperScroller({
        paper: paper,
        cursor: 'grab',
        autoResizePaper: true,
        baseWidth: 1,
        baseHeight: 1,
        inertia: { friction: 0.8 },
        borderless: true
    });
    canvas.appendChild(scroller.el);
    scroller.render().center();
    // Undo / Redo
    var commandManager = new dia.CommandManager({
        graph: graph,
        cmdBeforeAdd: function (eventName) {
            if (eventName === 'change:scrollTop')
                return false;
            return true;
        }
    });
    var toolbar = new ui.Toolbar({
        autoToggle: true,
        tools: [
            { type: 'undo' },
            { type: 'redo' },
            { type: 'zoomToFit' },
            { type: 'button', name: 'svg', text: 'Export SVG' }
        ],
        references: {
            commandManager: commandManager,
            paperScroller: scroller
        }
    });
    toolbar.on('svg:pointerclick', function () {
        format.toSVG(paper, function (svg) {
            new ui.Lightbox({
                image: 'data:image/svg+xml,' + encodeURIComponent(svg),
                downloadable: true,
                fileName: 'DataMapping'
            }).open();
        }, {
            preserveDimensions: true,
            convertImagesToDataUris: true,
            useComputedStyles: false
        });
    });
    toolbar.render();
    toolbar.el.style.height = toolbarHeight + 'px';
    canvas.appendChild(toolbar.el);
    // Scrollbars
    graph.on('add', function (cell) {
        if (cell.get('type') === 'mapping.Record') {
            cell.findView(paper).addTools(new dia.ToolsView({
                tools: [new elementTools.RecordScrollbar({})]
            }));
        }
    });
    // Decorators
    graph.on('add change:decorators', function (cell) {
        var decorators = cell.get('decorators');
        if (!util.isPlainObject(decorators))
            return;
        var view = cell.findView(paper);
        Decorator.remove(view);
        Object.keys(decorators).forEach(function (itemId) {
            var text = decorators[itemId];
            if (!text || !cell.item(itemId))
                return;
            Decorator.create(view, itemId, { text: text });
        });
    });
    commandManager.stopListening();
    loadExample(graph);
    commandManager.listen();
    // Register events
    paper.on('blank:pointerdown', function (evt) { return scroller.startPanning(evt); });
    paper.on('blank:mousewheel', function (evt, ox, oy, delta) {
        evt.preventDefault();
        zoom(ox, oy, delta);
    });
    paper.on('link:mousewheel', function (_, evt, ox, oy, delta) {
        evt.preventDefault();
        zoom(ox, oy, delta);
    });
    function zoom(x, y, delta) {
        scroller.zoom(delta * 0.2, { min: 0.4, max: 3, grid: 0.2, ox: x, oy: y });
    }
    paper.on('link:mouseenter', function (linkView) {
        showLinkTools(linkView);
    });
    paper.on('link:mouseleave', function (linkView) {
        linkView.removeTools();
    });
    paper.on('element:magnet:pointerdblclick', function (elementView, evt, magnet) {
        evt.stopPropagation();
        var model = elementView.model;
        itemEditAction(model, elementView.findAttribute('item-id', magnet));
    });
    paper.on('element:contextmenu', function (elementView, evt) {
        var model = elementView.model;
        var tools = model.getTools();
        if (tools) {
            evt.stopPropagation();
            elementActionPicker(elementView.el, elementView, tools);
        }
    });
    paper.on('element:magnet:contextmenu', function (elementView, evt, magnet) {
        var model = elementView.model;
        var itemId = elementView.findAttribute('item-id', magnet);
        var tools = model.getItemTools(itemId);
        if (tools) {
            evt.stopPropagation();
            itemActionPicker(magnet, elementView, elementView.findAttribute('item-id', magnet), tools);
        }
    });
    paper.on('element:pointerclick', function (elementView) {
        showElementTools(elementView);
    });
    paper.on('element:pointermove', function (view, evt, x, y) {
        var data = evt.data;
        var ghost = data.ghost;
        if (!ghost) {
            var position = view.model.position();
            ghost = view.vel.clone();
            ghost.attr('opacity', 0.3);
            ghost.appendTo(this.viewport);
            evt.data.ghost = ghost;
            evt.data.dx = x - position.x;
            evt.data.dy = y - position.y;
        }
        ghost.attr('transform', 'translate(' + [x - data.dx, y - data.dy] + ')');
    });
    paper.on('element:pointerup', function (view, evt, x, y) {
        var data = evt.data;
        if (data.ghost) {
            data.ghost.remove();
            view.model.position(x - data.dx, y - data.dy);
        }
    });
    paper.on('element:mousewheel', function (recordView, evt, x, y, delta) {
        evt.preventDefault();
        var record = recordView.model;
        if (!record.isEveryItemInView()) {
            record.setScrollTop(record.getScrollTop() + delta * 10);
        }
    });
    paper.on('element:decorator:pointerdown', function (recordView, evt, itemId) {
        var record = recordView.model;
        itemDecoratorEditAction(record, itemId);
    });
    paper.unfreeze();
    // Actions
    function showElementTools(elementView) {
        var element = elementView.model;
        var padding = util.normalizeSides(element.get('padding'));
        var isScrollable = (element.get('type') === 'mapping.Record');
        var transform = new ui.FreeTransform({
            cellView: elementView,
            allowRotation: false,
            resizeDirections: (isScrollable)
                ? ['top-left', 'top', 'top-right', 'right', 'bottom-right', 'bottom', 'bottom-left', 'left']
                : ['left', 'right'],
            minWidth: function () { return element.getMinimalSize().width; },
            minHeight: (isScrollable)
                ? padding.top + padding.bottom
                : 0
        });
        transform.render();
    }
    function showLinkTools(linkView) {
        var tools = new dia.ToolsView({
            tools: [
                new SourceArrowhead(),
                new TargetArrowhead(),
                new Button({
                    distance: '25%',
                    action: function () {
                        linkAction(this.model);
                    }
                })
            ]
        });
        linkView.addTools(tools);
    }
    function itemActionPicker(target, elementView, itemId, tools) {
        var element = elementView.model;
        var ctxToolbar = new ui.ContextToolbar({
            target: target,
            padding: 5,
            vertical: true,
            tools: tools
        });
        ctxToolbar.render();
        ctxToolbar.on({
            'action:remove': function () {
                element.startBatch('item-remove');
                element.removeItem(itemId);
                element.removeInvalidLinks();
                element.stopBatch('item-remove');
                ctxToolbar.remove();
            },
            'action:edit': function () {
                ctxToolbar.remove();
                itemEditAction(element, itemId);
            },
            'action:add-child': function () {
                ctxToolbar.remove();
                element.addItemAtIndex(itemId, Infinity, element.getDefaultItem());
                if (element.isItemCollapsed(itemId))
                    element.toggleItemCollapse(itemId);
            },
            'action:add-next-sibling': function () {
                ctxToolbar.remove();
                element.addNextSibling(itemId, element.getDefaultItem());
            },
            'action:add-prev-sibling': function () {
                ctxToolbar.remove();
                element.addPrevSibling(itemId, element.getDefaultItem());
            },
            'action:edit-decorator': function () {
                ctxToolbar.remove();
                itemDecoratorEditAction(element, itemId);
            }
        });
    }
    function elementActionPicker(target, elementView, tools) {
        var element = elementView.model;
        var ctxToolbar = new ui.ContextToolbar({
            target: target.firstChild,
            padding: 5,
            vertical: true,
            tools: tools
        });
        ctxToolbar.render();
        ctxToolbar.on({
            'action:remove': function () {
                ctxToolbar.remove();
                element.remove();
            },
            'action:add-item': function () {
                ctxToolbar.remove();
                element.addItemAtIndex(0, Infinity, element.getDefaultItem());
            }
        });
    }
    function itemDecoratorEditAction(element, itemId) {
        var _a;
        var config = (_a = {}, _a[itemId] = { type: 'content-editable', label: 'Decorator' }, _a);
        var path = ['decorators'];
        itemAction(element, config, path);
    }
    function itemEditAction(element, itemId) {
        var config = element.getInspectorConfig(itemId);
        var path = element.getItemPathArray(itemId);
        itemAction(element, config, path);
    }
    function itemAction(element, config, itemPath) {
        if (!config || !itemPath)
            return;
        var inspector = new ui.Inspector({
            cell: element,
            live: false,
            inputs: util.setByPath({}, itemPath, config)
        });
        inspector.render();
        inspector.el.style.position = 'relative';
        inspector.el.style.overflow = 'hidden';
        var dialog = new ui.Dialog({
            width: 300,
            title: 'Edit Item',
            closeButton: false,
            content: inspector.el,
            buttons: [{
                    content: 'Cancel',
                    action: 'cancel'
                }, {
                    content: '<span style="color:#fe854f">Change</span>',
                    action: 'change'
                }]
        });
        dialog.open();
        dialog.on({
            'action:cancel': function () {
                inspector.remove();
                dialog.close();
            },
            'action:change': function () {
                inspector.updateCell();
                inspector.remove();
                dialog.close();
            }
        });
        var input = inspector.el.querySelector('[contenteditable]');
        if (input) {
            var selection = window.getSelection();
            var range = document.createRange();
            range.selectNodeContents(input);
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }
    function linkAction(link) {
        var dialog = new ui.Dialog({
            title: 'Confirmation',
            width: 300,
            content: 'Are you sure you want to delete this link?',
            buttons: [
                { action: 'cancel', content: 'Cancel' },
                { action: 'remove', content: '<span style="color:#fe854f">Remove</span>' }
            ]
        });
        dialog.open();
        dialog.on({
            'action:remove': function () {
                link.remove();
                dialog.remove();
            },
            'action:cancel': function () {
                dialog.remove();
            }
        });
    }
};
